/**
 * Iter 268.f/g (Carlos): tras descargar el archivo TEF, preguntar si se registra
 * el pago de los documentos INCLUIDOS (los que pasaron al TXT) en Contabilidad
 * (egreso por proveedor en Alegra). Muestra un resumen (total $ + proveedores)
 * para una última revisión antes de confirmar.
 * Los documentos OMITIDOS del TXT no se contabilizan: quedan pendientes de pago.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { ScrollArea } from '../../ui/scroll-area';
import { Landmark } from 'lucide-react';

const fmt = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

export function TefContabConfirmDialog({ open, count, nombre, incluidas, busy, onClose, onConfirm }) {
  const items = incluidas || [];
  const total = items.reduce((s, i) => s + (Number(i.monto) || 0), 0);
  const porProv = {};
  items.forEach((i) => {
    const key = i.rut || i.proveedor || '—';
    if (!porProv[key]) porProv[key] = { nombre: i.proveedor || i.rut || '—', monto: 0, docs: 0 };
    porProv[key].monto += Number(i.monto) || 0;
    porProv[key].docs += 1;
  });
  const proveedores = Object.values(porProv).sort((a, b) => b.monto - a.monto);

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <AlertDialogContent data-testid="dialog-tef-contab" className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-indigo-600" />
            ¿Quieres registrar el pago de los documentos en Contabilidad?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Se registrará el pago de <strong>{count} documento(s)</strong>
                {nombre ? <> de la nómina <strong>{nombre}</strong></> : null} en{' '}
                <strong>Alegra</strong> (un egreso por proveedor), igual que al pagar una nómina.
              </p>

              <div className="rounded border bg-indigo-50 dark:bg-indigo-950/30 px-3 py-2 flex items-center justify-between">
                <span className="text-muted-foreground">Total a pagar</span>
                <span className="font-bold tabular-nums text-indigo-700 dark:text-indigo-300" data-testid="tef-contab-total">
                  {fmt(total)}
                </span>
              </div>

              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">
                  Proveedores ({proveedores.length})
                </div>
                <ScrollArea className="max-h-40 pr-3">
                  <ul className="space-y-1" data-testid="tef-contab-proveedores">
                    {proveedores.map((p, idx) => (
                      <li key={idx} className="flex items-center justify-between gap-2 rounded bg-slate-50 dark:bg-slate-900/40 px-2 py-1">
                        <span className="truncate" title={p.nombre}>
                          {p.nombre}
                          {p.docs > 1 ? <span className="text-muted-foreground"> · {p.docs} docs</span> : null}
                        </span>
                        <span className="shrink-0 tabular-nums font-medium">{fmt(p.monto)}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </div>

              <p className="text-[13px] text-muted-foreground">
                Solo se pagan los documentos que pasaron al archivo TXT. Los que quedaron omitidos{' '}
                <strong>no se contabilizan</strong>: siguen pendientes de pago por otro método.
                Si eliges <strong>No</strong>, el archivo ya se descargó pero el pago no se registrará.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="tef-contab-no">
            No
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={busy}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="tef-contab-si"
          >
            {busy ? 'Registrando…' : `Sí, pagar ${fmt(total)}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
